import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import * as Yup from "yup";

import { premiseNamesForTimeShift, regexForName } from "common/constant";
import { getConsumerNumberValidation } from "utils/getConsumerNumberValidation";
import { getDocumentsValidationSchema } from "utils/getDocumentsValidationSchema";

import { usePremiseTypeQuery } from "apis/PremiseType";
import { useApplicantTypeQuery } from "apis/ApplicantType";

const premisesForHP = ["temporary"];

export const useNetMeteringValidationSchema = () => {
  const [premiseIdsForHP, setPremiseIdsForHP] = useState([]);
  const [premiseIdsForTimeShift, setPremiseIdsForTimeShift] = useState([]);

  const formData = useSelector((state) => state.netMeteringConnection);

  const { data: { data: premiseType } = {} } = usePremiseTypeQuery({
    utilityId: 1,
  });
  const { data: { data: { items: applicantType = [] } = {} } = {} } =
    useApplicantTypeQuery();

  useEffect(() => {
    if (premiseType) {
      const updatedPremiseIdsForTimeShift = [];
      const updatedPremiseIds = [];
      premiseType.forEach((premise) => {
        if (premiseNamesForTimeShift.includes(premise.name.toLowerCase())) {
          updatedPremiseIdsForTimeShift.push(premise.id);
        }
        if (premisesForHP.includes(premise.name.toLowerCase())) {
          updatedPremiseIds.push(premise.id);
        }
      });

      setPremiseIdsForTimeShift(updatedPremiseIdsForTimeShift);
      setPremiseIdsForHP(updatedPremiseIds);
    }
  }, [premiseType]);

  const validationSchema = useMemo(
    () => [
      Yup.object({
        utilityConsumerId: Yup.string().required("Customer number is required"),
        applicant: Yup.object({
          panNumber: Yup.string()
                     .matches(
                       /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/,
                       "Please enter a valid Pan number"
                     ).required("Pan number is required"),
          isGSTRegistered: Yup.string().required("GST is required"),
          gstNumber: Yup.string()
            .test("isGstRequired", "", function (_, context) {
              const [{ value: { isGSTRegistered, gstNumber } } = {}] =
                context.from || [];
              if (isGSTRegistered && !gstNumber) {
                return context.createError({
                  message: "GST number is required",
                });
              }
              return true;
            })
            .matches(
              /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/,
              "Please enter a valid GST number"
            ),
        }),
      }),
      Yup.object({
        authorizedPerson: Yup.object({
          salutation: Yup.string().required("Salutation is required"),
          firstName: Yup.string()
            .matches(regexForName, "Please enter a valid first name")
            .required("First name is required"),
          middleName: Yup.string().matches(
            regexForName,
            "Please enter a valid middle name"
          ),
          lastName: Yup.string().matches(
            regexForName,
            "Please enter a valid last name"
          ),
          email: Yup.string()
            .email("Please enter valid email")
            .required("Email is required"),
          mobileNumber: Yup.string()
            .required("Mobile number is required")
            .matches(/^\d{10}$/, "Please enter valid mobile number"),
          landLine: Yup.string().matches(
            /^\d{8,11}$/,
            "Please enter a valid landline number"
          ),
          imageUrl: Yup.string().test(
            "imageRequired",
            "Please upload image",
            function () {
              const [{ value: current = null } = {}] = this.from || [];
              return !!(current?.image || current?.imageUrl);
            }
          ),
        }),
      }),
      Yup.object({
        utilityAddress: Yup.object(),
      }),

      Yup.object({
        solar: Yup.object({
          developerName: Yup.string().required(
            "Name of the Developer of the Solar project is required"
          ),
          gEDANumber: Yup.string().required(
            "Registration no. of GEDA is required"
          ),
          cEIApprovalNumber: Yup.string().required(
            "Approval No. of Chief Electrical Inspector is required"
          ),
          capacity: Yup.number()
            .required("Capacity of Solar Project is required")
            .test("isValidCapacity", "", function () {
              const [{ value: current = null } = {}] = this.from || [];
              if (current.capacity <= 0) {
                return this.createError({
                  message: "Capacity of Solar Project must be positive",
                });
              }
              if (current.capacity > 1000) {
                return this.createError({
                  message:
                    "Capacity of Solar Project should range from 1 to 1000",
                });
              }
              return true;
            }),
          proposedDate: Yup.date()
            .required("Proposed date of commissioning is required")
            .test("minStartDate", "", (_, context) => {
              const [{ value: { proposedDate } = {} } = {}] =
                context.from || [];
              if (!proposedDate?.isValid()) {
                return context.createError({
                  message: "Enter a valid date",
                });
              }
              if (proposedDate.isSameOrBefore(moment())) {
                return context.createError({
                  message: "Please enter a future date",
                });
              }
              return true;
            }),

          type: Yup.string().required(
            "Type of Solar Power Project is required"
          ),
          isRegisteredREC: Yup.boolean(),
          endUse: Yup.string().required(
            "End use of solar generated electricity is required"
          ),
          energySettlement: Yup.string().required(
            "Energy Settlement option is required"
          ),
          openAccess: Yup.string().required(
            "Details of power availing through Open Access is required"
          ),
          proposedProjectLocation: Yup.string().required(
            "Address of proposed project location is required"
          ),
        }),
      }),
      Yup.object({
        documents: getDocumentsValidationSchema(),
      }),
      Yup.object({
        declaration: Yup.object({
          isConsumerDuesOutStanding: Yup.boolean(),
          consumerDuesOutstanding: Yup.string().when(
            "isConsumerDuesOutstanding",
            {
              is: true,
              then: (schema) => getConsumerNumberValidation(schema),
              otherwise: (schema) => schema,
            }
          ),
          isPremisesDuesOutstanding: Yup.boolean(),
          premisesDuesOutstanding: Yup.string().when(
            "isPremisesDuesOutstanding",
            {
              is: true,
              then: (schema) => getConsumerNumberValidation(schema),
              otherwise: (schema) => schema,
            }
          ),
          isFirmDuesOutstanding: Yup.boolean(),
          firmDuesOutstanding: Yup.string().when("isFirmDuesOutstanding", {
            is: true,
            then: (schema) => getConsumerNumberValidation(schema),
            otherwise: (schema) => schema,
          }),
        }),
      }),
    ],
    [
      formData.connectionLoad.tention,
      formData.connectionLoad.isGreenPowerRequired,
      premiseIdsForHP,
      premiseIdsForTimeShift,
      applicantType,
    ]
  );

  const useNetMeteringValidationSchema = useCallback(
    (index) => validationSchema[index],
    [validationSchema]
  );
  return useNetMeteringValidationSchema;
};
